/**
 * API Schema Query Functions
 *
 * Loads the generated API schema index and provides query functions
 * for the `sentry schema` command. The schema is generated by
 * `script/generate-api-schema.ts` from Sentry's OpenAPI specification.
 */

import endpoints from "../generated/api-schema.json";

/** A single API endpoint entry from the generated schema */
export type ApiEndpoint = {
  /** SDK function name from @sentry/api (empty string if unmapped) */
  fn: string;
  /** HTTP method (GET, POST, PUT, DELETE, PATCH) */
  method: string;
  /** URL path template with {param} placeholders */
  path: string;
  /** Human-readable description of the endpoint */
  description: string;
  /** Path parameter names extracted from the URL template */
  pathParams: string[];
  /** Query parameter names from the OpenAPI spec */
  queryParams: string[];
  /** Whether the endpoint is deprecated */
  deprecated: boolean;
  /** Resource group name derived from URL (e.g., "projects", "issues") */
  resource: string;
  /** Human-readable operation ID from OpenAPI (e.g., "List an Organization's Issues") */
  operationId: string;
};

/** Summary of a resource group with its operations */
export type ResourceSummary = {
  /** Resource group name */
  name: string;
  /** Number of endpoints in this resource */
  endpointCount: number;
  /** List of distinct HTTP methods available */
  methods: string[];
};

/** Type the imported JSON array */
const schema = endpoints as ApiEndpoint[];

/** Get all unique resource names, sorted alphabetically */
export function getAllResources(): string[] {
  const resources = new Set(schema.map((e) => e.resource));
  return [...resources].sort();
}

/** Get all endpoints for a given resource name (case-insensitive) */
export function getEndpointsByResource(resource: string): ApiEndpoint[] {
  const lower = resource.toLowerCase();
  return schema.filter((e) => e.resource.toLowerCase() === lower);
}

/**
 * Get a specific endpoint by resource + operationId substring (case-insensitive).
 * Prefers operationIds that start with the query over substring matches.
 */
export function getEndpoint(
  resource: string,
  operationQuery: string
): ApiEndpoint | undefined {
  const lowerResource = resource.toLowerCase();
  const lowerQuery = operationQuery.toLowerCase();

  const candidates = schema.filter(
    (e) => e.resource.toLowerCase() === lowerResource
  );

  // Priority 1: operationId starts with the query (e.g., "list" → "List an Org's Issues")
  const startMatch = candidates.find(
    (e) =>
      e.operationId.toLowerCase().startsWith(lowerQuery) ||
      e.fn.toLowerCase().startsWith(lowerQuery)
  );
  if (startMatch) {
    return startMatch;
  }

  // Priority 2: substring match anywhere in operationId or fn
  return candidates.find(
    (e) =>
      e.operationId.toLowerCase().includes(lowerQuery) ||
      e.fn.toLowerCase().includes(lowerQuery)
  );
}

/** Search endpoints by query string (matches fn, path, description, resource, operationId) */
export function searchEndpoints(query: string): ApiEndpoint[] {
  const lower = query.toLowerCase();
  return schema.filter(
    (e) =>
      e.fn.toLowerCase().includes(lower) ||
      e.path.toLowerCase().includes(lower) ||
      e.description.toLowerCase().includes(lower) ||
      e.resource.toLowerCase().includes(lower) ||
      e.operationId.toLowerCase().includes(lower)
  );
}

/** Get all endpoints (returns a readonly reference to the internal array) */
export function getAllEndpoints(): readonly ApiEndpoint[] {
  return schema;
}

/** Get resource summary: name, endpoint count, and methods for each resource */
export function getResourceSummaries(): ResourceSummary[] {
  const map = new Map<
    string,
    { endpointCount: number; methods: Set<string> }
  >();

  for (const endpoint of schema) {
    const existing = map.get(endpoint.resource);
    if (existing) {
      existing.endpointCount += 1;
      existing.methods.add(endpoint.method);
    } else {
      map.set(endpoint.resource, {
        endpointCount: 1,
        methods: new Set([endpoint.method]),
      });
    }
  }

  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, info]) => ({
      name,
      endpointCount: info.endpointCount,
      methods: [...info.methods].sort(),
    }));
}
